import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  TextField,
  Typography,
  Divider
} from '@mui/material';
import { FilterAlt } from '@mui/icons-material';

interface FilterOptions {
  eyeType: 'all' | 'left' | 'right';
  dateFrom: string | null;
  dateTo: string | null;
  month: number | null;
  year: number | null;
}

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterOptions) => void;
  currentFilters: FilterOptions;
}

const FilterDialog: React.FC<FilterDialogProps> = ({ 
  open, 
  onClose, 
  onApply, 
  currentFilters 
}) => {
  const [filters, setFilters] = useState<FilterOptions>(currentFilters);

  const handleChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters: FilterOptions = {
      eyeType: 'all',
      dateFrom: null,
      dateTo: null,
      month: null,
      year: null
    };
    setFilters(resetFilters);
    onApply(resetFilters);
    onClose();
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ bgcolor: '#2A2A2A', color: '#E0E0E0', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterAlt /> Filter Tests
      </DialogTitle>
      <DialogContent sx={{ bgcolor: '#2A2A2A', color: '#E0E0E0', pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="eye-type-label">Eye Type</InputLabel>
            <Select
              labelId="eye-type-label"
              value={filters.eyeType}
              label="Eye Type"
              onChange={(e) => handleChange('eyeType', e.target.value)}
            >
              <MenuItem value="all">All Eyes</MenuItem>
              <MenuItem value="left">Left Eye</MenuItem>
              <MenuItem value="right">Right Eye</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Date Range</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="From"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom || ''}
              onChange={(e) => handleChange('dateFrom', e.target.value || null)}
            />
            <TextField
              label="To"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo || ''}
              onChange={(e) => handleChange('dateTo', e.target.value || null)}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2, bgcolor: '#3A3A3A' }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick Filters</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="month-label">Month</InputLabel>
            <Select
              labelId="month-label"
              value={filters.month !== null ? filters.month : ''}
              label="Month"
              onChange={(e) => handleChange('month', e.target.value !== '' ? Number(e.target.value) : null)}
            >
              <MenuItem value="">All Months</MenuItem>
              {months.map(month => (
                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              labelId="year-label"
              value={filters.year !== null ? filters.year : ''}
              label="Year"
              onChange={(e) => handleChange('year', e.target.value !== '' ? Number(e.target.value) : null)}
            >
              <MenuItem value="">All Years</MenuItem>
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: '#2A2A2A', px: 3, py: 2 }}>
        <Button onClick={handleReset} variant="outlined" color="error">
          Reset
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterDialog; 